import path from 'path';

import axios, { AxiosInstance } from 'axios';

import { sessionStore } from '~atoms/sessionAtom';
import { pagePaths } from '~constants';
import { OffsetPagedResults } from '~interfaces';
import { OffsetPagedResponse, SkcOffsetPagedResponse } from '~interfaces/responses';

import ApiError from './apiError';

/**
 * A base service class to e.g. give the ability to cancel requests
 */
abstract class SkopeiConnectService {
	protected client: AxiosInstance;

	protected readonly path: string = '';

	protected _controller = new AbortController();

	constructor() {
		this.client = axios.create({
			baseURL: new URL(import.meta.env.VITE_SKOPEI_CONNECT_SERVICE_URL).href,
		});

		this.setupInterceptors();
	}

	/**
	 * Creates an endpoint to be called by the client. This injects the path of the current services
	 * in between the version and the pathSegments.
	 *
	 * @example
	 * protected readonly path = 'users';
	 * this.createUrl('v1', ['me'], true) // output: /v1/internal/users/me
	 * this.createUrl('v2', [userId, 'assign']) // output: /v2/users/53/assign
	 *
	 * @param version The version of the API to call
	 * @param pathSegments The path segments to construct the endpoint
	 * @param isInternal Will add internal before the path
	 *
	 * @returns The full URL including the service's path
	 */
	public createEndpoint(
		version: 'v1' | 'v2',
		pathSegments: string[] = [],
		isInternal: boolean = false
	): string {
		return path.join(version, isInternal ? 'internal' : '', this.path, ...pathSegments);
	}

	/**
	 * Abort the current get requests.
	 * TODO: should also do this for the others (post, put etc)?
	 * @param reason The reason for the abort
	 */
	public abortCurrentRequest(reason?: string) {
		this._controller.abort(reason);
		this._controller = new AbortController();
	}

	public setupInterceptors() {
		// Attach latest token to every request
		this.client.interceptors.request.use(async (config) => {
			const accessToken = (await sessionStore.getSnapshot()).accessToken;
			try {
				if (accessToken) {
					config.headers['Authorization'] = `Bearer ${accessToken}`;
				}
				return config;
			} catch (error) {
				console.error('[Auth] Error getting user', error);
				return config;
			}
		});

		// Single-flight refresh deduplication across requests
		let refreshPromise: Promise<unknown> | null = null;

		// For testing refresh flow
		// let hasRefreshed = false;
		// this.client.interceptors.request.use((request) => {
		// 	if (request.url?.includes('users/64') && !hasRefreshed) {
		// 		hasRefreshed = true;
		// 		const error = new AxiosError('Simulated 401', 'ERR_BAD_REQUEST', request, null, {
		// 			status: 401,
		// 			statusText: 'Unauthorized',
		// 			data: { message: 'Simulated 401' },
		// 			headers: {},
		// 			config: request,
		// 		});

		// 		return Promise.reject(error);
		// 	}

		// 	return request;
		// });

		// Refresh on 401 and retry once
		this.client.interceptors.response.use(
			(response) => response,
			async (error) => {
				const status = error?.response?.status;
				const isUnauthorized = status === 401;
				const originalRequest = error?.config;

				if (!isUnauthorized || originalRequest?._retry) {
					return Promise.reject(new ApiError(error));
				}

				if (!refreshPromise) {
					console.info('[Auth] No prior refresh');
					// Create the promise and assign it
					const session = await sessionStore.getSnapshot();
					refreshPromise = sessionStore.refreshTokens(session);
				}

				// Wait for the existing or new refresh attempt to complete
				try {
					await refreshPromise;
				} catch (_error) {
					window.location.assign(pagePaths.AuthLogin);
					refreshPromise = null;
					return Promise.reject(new ApiError(error));
				}

				// Retry the original request with the new token
				const { accessToken } = await sessionStore.getSnapshot();
				if (accessToken) {
					refreshPromise = null;
					originalRequest._retry = true;
					originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
					return this.client.request(originalRequest);
				} else {
					window.location.assign(pagePaths.AuthLogin);
				}

				return Promise.reject(error);
			}
		);
	}

	protected mapPagedResponse<T extends object, U extends object>(
		response: OffsetPagedResponse<T>,
		mapFunction: (value: T) => U
	): OffsetPagedResults<U> {
		return {
			...response,
			results: response.results.map((el) => mapFunction(el)),
		};
	}

	protected mapSkcOffsetPagedResponse<T extends object, U extends object>(
		response: SkcOffsetPagedResponse<T>,
		mapFunction: (value: T) => U
	): OffsetPagedResults<U> {
		// TODO: remove this once we have migrated to the new shape
		// Prefer new shape: { items, pageInfo }
		if (response.pageInfo && response.items) {
			return {
				hasMore: response.pageInfo.hasMore,
				total: response.pageInfo.totalCount,
				page: response.pageInfo.pageNumber,
				pageSize: response.pageInfo.pageSize,
				results: response.items.map((el) => mapFunction(el)),
			};
		}

		// Fallback to legacy { data, meta }
		const data = response.data ?? [];
		const meta = response.meta ?? {
			pageNumber: 0,
			pageSize: data.length,
			total: data.length,
		};

		const total = meta.total < 0 ? 0 : meta.total;
		const count = (meta.pageNumber - 1) * meta.pageSize + data.length;

		return {
			hasMore: count < total,
			total,
			page: meta.pageNumber,
			pageSize: meta.pageSize,
			results: data.map((el) => mapFunction(el)),
		};
	}
}

export default SkopeiConnectService;
